import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatAxiosError } from "@/lib/api";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { useBranding } from "@/context/BrandingContext";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const branding = useBranding();
  const [validating, setValidating] = useState(true);
  const [validEmail, setValidEmail] = useState("");
  const [linkError, setLinkError] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [valid, setValid] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/auth/reset-password/${token}/validate`);
        setValidEmail(r.data.email || "");
      } catch (e) { setLinkError(formatAxiosError(e)); }
      finally { setValidating(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) { setErr("Passwords don't match"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setOk(true);
      setTimeout(() => nav("/login"), 2000);
    } catch (er) { setErr(formatAxiosError(er)); }
    finally { setBusy(false); }
  };

  if (validating) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Validating link…</div>;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-white">
      <div className="max-w-md w-full">
        <div className="eyebrow mb-3">Set a new password</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-tight">
          {ok ? "All done!" : "Choose a new password"}
        </h1>
        {linkError ? (
          <div className="mt-6 border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] p-4 text-sm" data-testid="reset-link-error">
            {linkError}
            <div className="mt-3"><Link to="/forgot-password" className="underline">Request a new link</Link></div>
          </div>
        ) : ok ? (
          <div className="mt-6 border border-green-500 bg-green-50 text-green-800 p-4 text-sm flex items-start gap-3" data-testid="reset-ok">
            <CheckCircle size={20} weight="fill" />
            <div>
              <div className="font-semibold">Your password has been updated.</div>
              Redirecting you to sign in…
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <p className="text-sm text-muted-foreground">Setting a new password for <b>{validEmail}</b>. All other sessions on this account will be signed out.</p>
            <div>
              <label className="eyebrow block mb-2">New password</label>
              <input
                type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)}
                className="w-full border border-border bg-white px-4 py-3 text-sm" data-testid="reset-new-pw"
              />
              <PasswordStrengthMeter value={pwd} onValidityChange={setValid} />
            </div>
            <div>
              <label className="eyebrow block mb-2">Confirm new password</label>
              <input
                type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full border border-border bg-white px-4 py-3 text-sm" data-testid="reset-confirm-pw"
              />
              {confirm && pwd !== confirm && <div className="text-xs text-red-600 mt-1">Passwords don't match</div>}
            </div>
            {err && <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-4 py-2 text-sm">{err}</div>}
            <button
              type="submit" disabled={busy || !valid || pwd !== confirm}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-semibold uppercase tracking-wide hover:opacity-90 transition disabled:opacity-50"
              style={{ backgroundColor: branding?.brand_mark_color || "#002FA7" }}
              data-testid="reset-submit"
            >
              {busy ? "Saving…" : "Reset password"} <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
